import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation, Autoplay } from 'swiper/modules';
import { Link } from 'react-router-dom';
import api, { API_BASE_URL } from '../api';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await api.get('/api/products/categories');
        setCategories(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto p-14 md:px-20 lg:px-32 w-full animate-pulse">
        <div className="h-10 bg-gray-200 rounded w-1/3 mx-auto mb-12"></div>
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-2xl h-64"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="container mt-4  mx-auto py-14 px-4 md:px-20 lg:px-32 w-full overflow-hidden"
      id="categories"
      style={{
        backgroundImage: 'url("https://www.transparenttextures.com/patterns/graphy-light.png")',
        backgroundAttachment: 'fixed'
      }}
    >
      <h1 className="text-3xl sm:text-4xl font-extrabold mb-12 text-customRed text-center">
        Product Categories
      </h1>

      {/* Grid View for Larger Screens */}
      <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {categories.map((category, index) => (
          <div
            key={index}
            className="group flex flex-col h-full bg-[#f3f4f6] rounded-2xl shadow-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
          >
            <Link to={`/products?category=${category.name}`} className="p-6 pb-2">
              <div className="overflow-hidden rounded-xl bg-white shadow-inner h-64 flex items-center justify-center">
                <img
                  src={category.image.startsWith('http') ? category.image : `${API_BASE_URL}/storage/${category.image}`}
                  alt={category.name}
                  className="w-full h-full object-contain p-4 transform transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            </Link>
            <div className="p-6 pt-2 flex-grow flex flex-col justify-between">
              <Link
                to={`/products?category=${category.name}`}
                className="flex items-center justify-between no-underline group/link w-full h-full"
              >
                <span className="text-xl font-bold text-[#374151] group-hover/link:text-customRed transition-colors duration-300">
                  {category.name}
                </span>
                <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-customRed text-white shadow-md transform transition-transform duration-300 group-hover/link:rotate-45">
                   <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M7 17l9-9m0 0H7m9 0v9"
                    />
                  </svg>
                </div>
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Swiper View for Smaller Screens */}
      <div className="block md:hidden relative">
        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={20}
          slidesPerView={1}
          navigation={{
            nextEl: '.swiper-button-next-custom',
            prevEl: '.swiper-button-prev-custom',
          }}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            640: { slidesPerView: 2 },
          }}
          grabCursor={true}
          className="w-full pb-10"
        >
          {categories.map((category, index) => (
            <SwiperSlide key={index} className="!h-auto">
              <div
                className="group flex flex-col h-full bg-[#f3f4f6] rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <Link to={`/products?category=${category.name}`} className="p-6 pb-2">
                  <div className="overflow-hidden rounded-xl bg-white shadow-inner h-64 flex items-center justify-center">
                    <img
                      src={category.image.startsWith('http') ? category.image : `${API_BASE_URL}/storage/${category.image}`}
                      alt={category.name}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                </Link>
                <div className="p-6 pt-2 flex-grow flex flex-col justify-between">
                  <Link
                    to={`/products?category=${category.name}`}
                    className="flex items-center justify-between no-underline w-full h-full"
                  >
                    <span className="text-xl font-bold text-[#374151]">
                      {category.name}
                    </span>
                    <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-customRed text-white">
                       <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M7 17l9-9m0 0H7m9 0v9"
                        />
                      </svg>
                    </div>
                  </Link>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        
        {/* Swiper Navigation Arrows - Positioned absolutely relative to the block container to reach edges */}
        <div className="swiper-button-next-custom absolute top-1/2 -translate-y-1/2 right-0 z-10 text-customRed shadow-lg bg-white/95 rounded-full w-12 h-12 flex items-center justify-center cursor-pointer border border-gray-200 transition-all hover:bg-white active:scale-95">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <div className="swiper-button-prev-custom absolute top-1/2 -translate-y-1/2 left-0 z-10 text-customRed shadow-lg bg-white/95 rounded-full w-12 h-12 flex items-center justify-center cursor-pointer border border-gray-200 transition-all hover:bg-white active:scale-95">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default Categories;
